import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
  domtools,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TKindFilter = interfaces.TPackageKind | 'all';

@customElement('dotglobal-packages')
export class DotglobalPackages extends DeesElement {
  @property({ type: String })
  public accessor kind: interfaces.TPackageKind | '' = '';

  @property({ type: String })
  public accessor owner = '';

  @property({ type: String })
  public accessor name = '';

  @property({ type: String })
  public accessor csrfToken = '';

  @state()
  private accessor packages: interfaces.IPackageSummary[] = [];

  @state()
  private accessor detail: interfaces.IPackageDetail | null = null;

  @state()
  private accessor loading = false;

  @state()
  private accessor actionPending = false;

  @state()
  private accessor errorMessage = '';

  @state()
  private accessor query = '';

  @state()
  private accessor ownerFilter = '';

  @state()
  private accessor kindFilter: TKindFilter = 'all';

  private loadId = 0;
  private authEpoch = 0;

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; flex: 1; }
      .banner { border-bottom: 1px solid var(--border-default); background: var(--bg-secondary); }
      .banner h1 { margin: 0; font-size: 30px; letter-spacing: -0.025em; }
      .banner p { margin: 8px 0 0; color: var(--text-secondary); font-size: 14px; line-height: 1.5; }
      .back {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 14px;
        color: var(--text-secondary);
        font-size: 12.5px;
      }
      .back:hover { color: var(--text-primary); }
      .toolbar {
        display: grid;
        grid-template-columns: minmax(180px, 1fr) minmax(150px, 240px) auto;
        gap: 10px;
        align-items: end;
        margin-bottom: 16px;
      }
      .field { display: grid; gap: 5px; min-width: 0; }
      .field label { color: var(--text-muted); font-size: 11.5px; font-weight: 600; }
      .field input {
        width: 100%;
        box-sizing: border-box;
        height: 36px;
        padding: 0 11px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        color: var(--text-primary);
        outline: none;
      }
      .field input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 16%, transparent); }
      .seg { display: inline-flex; flex-wrap: wrap; gap: 4px; margin-bottom: 16px; }
      .seg button {
        padding: 6px 12px;
        border: 1px solid var(--border-default);
        border-radius: 999px;
        color: var(--text-secondary);
        font-size: 12px;
      }
      .seg button.on { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 12%, transparent); color: var(--accent); }
      .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; }
      .pkg {
        display: grid;
        gap: 12px;
        padding: 16px;
        text-align: left;
        transition: border-color 0.12s, transform 0.12s, box-shadow 0.12s;
      }
      .pkg:hover { border-color: var(--accent); transform: translateY(-1px); box-shadow: var(--shadow-sm); }
      .pkg-title { display: flex; align-items: center; gap: 9px; min-width: 0; }
      .pkg-title strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .kind {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 2px 7px;
        border-radius: 5px;
        background: var(--bg-tertiary);
        color: var(--text-secondary);
        font: 600 10.5px var(--font-mono);
        text-transform: uppercase;
      }
      .meta { display: flex; flex-wrap: wrap; gap: 10px; color: var(--text-muted); font-size: 11.5px; }
      .meta span { display: inline-flex; align-items: center; gap: 5px; }
      .desc { min-height: 20px; color: var(--text-secondary); font-size: 12.5px; line-height: 1.5; }
      .empty, .loading, .error { padding: 54px 20px; text-align: center; color: var(--text-muted); }
      .error { color: var(--error); }
      .detail-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; flex-wrap: wrap; }
      .detail-title { display: grid; gap: 8px; min-width: 0; }
      .detail-title h1 { margin: 0; font: 700 25px var(--font-mono); overflow-wrap: anywhere; }
      .detail-actions { display: flex; gap: 8px; flex-wrap: wrap; }
      .stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin: 16px 0; }
      .stat { padding: 14px; }
      .stat b { display: block; margin-bottom: 4px; font-size: 18px; }
      .stat span { color: var(--text-muted); font-size: 11.5px; }
      .setup { padding: 15px; margin-bottom: 16px; }
      .setup h2, .items h2 { margin: 0 0 12px; font-size: 14px; }
      .command {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 9px 10px;
        border-radius: 6px;
        background: var(--code-bg);
        font: 12px var(--font-mono);
      }
      .command code { flex: 1; overflow: auto; white-space: nowrap; }
      .item { display: grid; grid-template-columns: minmax(120px, 1fr) minmax(150px, 1.4fr) auto auto; gap: 12px; align-items: center; padding: 11px 14px; border-bottom: 1px solid var(--border-subtle); }
      .item:last-child { border-bottom: 0; }
      .reference { font: 600 12.5px var(--font-mono); overflow-wrap: anywhere; }
      .digest { color: var(--text-muted); font: 10.5px var(--font-mono); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .tag { display: inline-flex; margin-left: 5px; padding: 1px 5px; border-radius: 999px; background: color-mix(in srgb, var(--accent) 12%, transparent); color: var(--accent); font-size: 10px; }
      @media (max-width: 820px) {
        .toolbar { grid-template-columns: 1fr; }
        .stats { grid-template-columns: 1fr; }
        .item { grid-template-columns: 1fr auto; }
        .item .digest { grid-column: 1 / -1; }
      }
    `,
  ];

  protected willUpdate(changedPropertiesArg: Map<PropertyKey, unknown>): void {
    if (changedPropertiesArg.has('csrfToken')) {
      this.authEpoch += 1;
      this.loadId += 1;
      this.packages = [];
      this.detail = null;
      this.loading = false;
      this.actionPending = false;
      this.errorMessage = '';
    }
  }

  protected updated(changedPropertiesArg: Map<PropertyKey, unknown>): void {
    if (
      changedPropertiesArg.has('kind')
      || changedPropertiesArg.has('owner')
      || changedPropertiesArg.has('name')
      || changedPropertiesArg.has('csrfToken')
    ) {
      this.ownerFilter = this.owner;
      this.kindFilter = this.kind || 'all';
      void this.load();
    }
  }

  public render(): TemplateResult {
    return this.kind && this.owner && this.name
      ? this.renderDetail()
      : this.renderCatalog();
  }

  private renderCatalog(): TemplateResult {
    const rows = this.packages.filter((entry) =>
      this.kindFilter === 'all' || entry.kind === this.kindFilter
    );
    return html`
      <div class="stack" style="flex: 1">
        <div class="banner">
          <div class="page" style="padding-top: 26px; padding-bottom: 26px">
            <button class="back" @click=${() => this.navigate('#/')}>${this.icon('ChevronLeft')} Back to explore</button>
            <h1>Packages</h1>
            <p>Discover and manage npm packages, generic artifacts, and OCI images hosted on code.foss.global.</p>
          </div>
        </div>
        <div class="page content">
          <div class="toolbar">
            <div class="field"><label>Search</label><input .value=${this.query} placeholder="Package or owner" @input=${(eventArg: Event) => { this.query = (eventArg.currentTarget as HTMLInputElement).value; }} @keydown=${(eventArg: KeyboardEvent) => { if (eventArg.key === 'Enter') void this.load(); }} /></div>
            <div class="field"><label>Owner</label><input .value=${this.ownerFilter} placeholder="All public owners" @input=${(eventArg: Event) => { this.ownerFilter = (eventArg.currentTarget as HTMLInputElement).value; }} /></div>
            <button class="btn primary" ?disabled=${this.loading} @click=${() => void this.load()}>${this.icon('Search')} ${this.loading ? 'Loading…' : 'Search'}</button>
          </div>
          <div class="seg">
            ${(['all', 'npm', 'generic', 'oci'] as TKindFilter[]).map((kind) => html`<button class=${this.kindFilter === kind ? 'on' : ''} @click=${() => { this.kindFilter = kind; }}>${kind === 'all' ? 'All formats' : this.kindLabel(kind)}</button>`)}
          </div>
          ${this.errorMessage ? html`<div class="card error">${this.errorMessage}</div>` : ''}
          ${this.loading && this.packages.length === 0
            ? html`<div class="loading">Loading packages…</div>`
            : rows.length
            ? html`<div class="grid">${rows.map((entry) => this.renderPackageCard(entry))}</div>`
            : html`<div class="card empty">No packages match this view.</div>`}
        </div>
      </div>
    `;
  }

  private renderPackageCard(packageArg: interfaces.IPackageSummary): TemplateResult {
    return html`
      <button class="card pkg" @click=${() => this.navigate(this.packageHash(packageArg))}>
        <div class="pkg-title">
          <span class="kind">${this.icon(this.kindIcon(packageArg.kind), 12)} ${packageArg.kind}</span>
          <strong>${packageArg.owner}/${packageArg.name}</strong>
          <span class="grow"></span>
          ${packageArg.visibility === 'private' ? this.icon('Lock', 13) : this.icon('Globe2', 13)}
        </div>
        <div class="desc">${packageArg.description || `${this.kindLabel(packageArg.kind)} hosted by ${packageArg.owner}`}</div>
        <div class="meta">
          <span>${this.icon('Layers', 12)} ${packageArg.itemCount} items</span>
          <span>${this.icon('HardDrive', 12)} ${this.formatBytes(packageArg.retainedBytes)}</span>
          ${packageArg.latestReference ? html`<span>${this.icon('Tag', 12)} ${packageArg.latestReference}</span>` : ''}
          <span>${this.icon('Clock3', 12)} ${this.timeAgo(packageArg.updatedAt)}</span>
        </div>
      </button>
    `;
  }

  private renderDetail(): TemplateResult {
    const detail = this.detail;
    const packageData = detail?.package;
    const canManage = Boolean(this.csrfToken) && detail?.canManage === true;
    return html`
      <div class="page content">
        <button class="back" @click=${() => this.navigate('#/packages')}>${this.icon('ChevronLeft')} All packages</button>
        ${packageData
          ? html`
              <div class="detail-head">
                <div class="detail-title">
                  <div class="row" style="gap: 8px"><span class="kind">${this.icon(this.kindIcon(packageData.kind), 12)} ${packageData.kind}</span><span class="badge def sq">${packageData.visibility}</span></div>
                  <h1>${packageData.owner}/${packageData.name}</h1>
                  ${packageData.description ? html`<span class="muted">${packageData.description}</span>` : ''}
                </div>
                ${canManage ? html`<div class="detail-actions"><button class="btn sm" ?disabled=${this.actionPending} @click=${() => void this.setVisibility(packageData.visibility === 'public' ? 'private' : 'public')}>${this.icon(packageData.visibility === 'public' ? 'Lock' : 'Globe2')} Make ${packageData.visibility === 'public' ? 'private' : 'public'}</button></div>` : ''}
              </div>
              <div class="stats">
                <div class="card stat"><b>${packageData.itemCount}</b><span>Published items</span></div>
                <div class="card stat"><b>${this.formatBytes(packageData.retainedBytes)}</b><span>Retained storage</span></div>
                <div class="card stat"><b>${packageData.latestReference ?? '—'}</b><span>Latest reference</span></div>
              </div>
            `
          : ''}
        ${this.errorMessage ? html`<div class="card error">${this.errorMessage}</div>` : ''}
        ${this.loading && !detail ? html`<div class="loading">Loading package…</div>` : ''}
        ${detail ? html`
          ${detail.setup.length ? html`<div class="card setup"><h2>Use this package</h2><div class="stack" style="gap: 8px">${detail.setup.map((command) => html`<div class="command"><code>${command}</code><button class="iconbtn" title="Copy command" @click=${() => this.copy(command)}>${this.icon('Copy', 13)}</button></div>`)}</div></div>` : ''}
          <div class="card items">
            <div style="padding: 14px 14px 0"><h2>Published items</h2></div>
            ${detail.items.length ? detail.items.map((item) => this.renderItem(item, canManage)) : html`<div class="empty">No published items remain.</div>`}
          </div>
        ` : ''}
      </div>
    `;
  }

  private renderItem(itemArg: interfaces.IPackageItem, canManageArg: boolean): TemplateResult {
    return html`
      <div class="item">
        <div><span class="reference">${itemArg.version ?? itemArg.reference}</span>${itemArg.tags?.map((tag) => html`<span class="tag">${tag}</span>`)}</div>
        <div class="digest" title=${itemArg.digest ?? itemArg.filename ?? ''}>${itemArg.filename ?? itemArg.digest ?? itemArg.mediaType ?? ''}</div>
        <span class="muted" style="font-size: 11.5px">${this.formatBytes(itemArg.sizeBytes)}</span>
        ${canManageArg ? html`<button class="btn sm danger" ?disabled=${this.actionPending} @click=${() => void this.deleteItem(itemArg.reference)}>${this.icon('Trash2', 12)} Delete</button>` : html`<span class="muted" style="font-size: 11.5px">${this.timeAgo(itemArg.createdAt)}</span>`}
      </div>
    `;
  }

  private async load(): Promise<void> {
    const loadId = ++this.loadId;
    const authEpoch = this.authEpoch;
    this.loading = true;
    this.errorMessage = '';
    try {
      if (this.kind && this.owner && this.name) {
        const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_BrowserPackageGet>('/typedrequest', 'browserPackageGet');
        const response = await request.fire({
          kind: this.kind,
          owner: this.owner,
          name: this.name,
        });
        if (loadId === this.loadId && authEpoch === this.authEpoch) {
          this.detail = response.detail;
        }
      } else {
        const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_BrowserPackageList>('/typedrequest', 'browserPackageList');
        const owner = this.ownerFilter.trim() || undefined;
        const response = await request.fire({
          owner,
          query: this.query.trim() || undefined,
          limit: 100,
        });
        if (loadId === this.loadId && authEpoch === this.authEpoch) {
          this.packages = response.packages;
        }
      }
    } catch (error) {
      if (loadId === this.loadId && authEpoch === this.authEpoch) {
        this.errorMessage = error instanceof Error ? error.message : 'Unable to load packages.';
        this.requestAuthRefresh();
      }
    } finally {
      if (loadId === this.loadId && authEpoch === this.authEpoch) {
        this.loading = false;
      }
    }
  }

  private async setVisibility(visibilityArg: interfaces.TPackageVisibility): Promise<void> {
    if (!this.kind || !this.owner || !this.name || !this.csrfToken) return;
    const authEpoch = this.authEpoch;
    this.actionPending = true;
    this.errorMessage = '';
    try {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_BrowserPackageVisibilitySet>('/typedrequest', 'browserPackageVisibilitySet');
      const response = await request.fire({ kind: this.kind, owner: this.owner, name: this.name, visibility: visibilityArg, csrfToken: this.csrfToken });
      if (authEpoch === this.authEpoch) {
        this.detail = response.detail;
      }
    } catch (error) {
      if (authEpoch === this.authEpoch) {
        this.errorMessage = error instanceof Error ? error.message : 'Unable to change package visibility.';
        this.requestAuthRefresh();
      }
    } finally {
      if (authEpoch === this.authEpoch) {
        this.actionPending = false;
      }
    }
  }

  private async deleteItem(referenceArg: string): Promise<void> {
    if (!this.kind || !this.owner || !this.name || !this.csrfToken) return;
    if (!globalThis.confirm(`Delete ${referenceArg}? This cannot be undone.`)) return;
    const authEpoch = this.authEpoch;
    this.actionPending = true;
    this.errorMessage = '';
    try {
      const request = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_BrowserPackageItemDelete>('/typedrequest', 'browserPackageItemDelete');
      await request.fire({ kind: this.kind, owner: this.owner, name: this.name, reference: referenceArg, csrfToken: this.csrfToken });
      if (authEpoch === this.authEpoch) {
        await this.load();
      }
    } catch (error) {
      if (authEpoch === this.authEpoch) {
        this.errorMessage = error instanceof Error ? error.message : 'Unable to delete package item.';
        this.requestAuthRefresh();
      }
    } finally {
      if (authEpoch === this.authEpoch) {
        this.actionPending = false;
      }
    }
  }

  private packageHash(packageArg: interfaces.IPackageSummary): string {
    return `#/packages/${packageArg.kind}/${encodeURIComponent(packageArg.owner)}/${encodeURIComponent(packageArg.name)}`;
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private requestAuthRefresh(): void {
    if (!this.csrfToken) {
      return;
    }
    this.dispatchEvent(new CustomEvent('dotglobal-auth-refresh', {
      bubbles: true,
      composed: true,
    }));
  }

  private copy(textArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-copy', { detail: { text: textArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string, sizeArg = 14): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${sizeArg}></dees-icon>`;
  }

  private kindIcon(kindArg: interfaces.TPackageKind): string {
    return kindArg === 'oci' ? 'Container' : kindArg === 'npm' ? 'Boxes' : 'PackageOpen';
  }

  private kindLabel(kindArg: interfaces.TPackageKind): string {
    return kindArg === 'oci' ? 'Container images' : kindArg === 'npm' ? 'npm packages' : 'Generic packages';
  }

  private formatBytes(bytesArg: number): string {
    if (bytesArg < 1024) return `${bytesArg} B`;
    const units = ['KiB', 'MiB', 'GiB', 'TiB'];
    let value = bytesArg / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
    return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
  }

  private timeAgo(timestampArg: string): string {
    const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(timestampArg)) / 1000));
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(timestampArg).toLocaleDateString();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-packages': DotglobalPackages;
  }
}
